import { EditorState } from "prosemirror-state";
import { Node as ProsemirrorNode } from "prosemirror-model";
import { mentionInputSchema } from "./schema";
import { MentionNodeAttributes } from "./schema/nodes/mention";
import { ensureNotNil } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";

export enum MentionKind {
	User = 1
}

export type Mention = {
	kind: MentionKind.User;
	from: number;
	to: number;
	userId: number;
};

export type TextWithMentions = {
	text: string;
	mentions: readonly Mention[];
};

export const userMentionType = "@" as const;

export function isTextWithMentions(value: unknown): value is TextWithMentions {
	if (typeof value !== "object" || value === null) {
		return false;
	}

	const castedValue = value as TextWithMentions;

	if (typeof castedValue.text !== "string") {
		return false;
	}

	if (!Array.isArray(castedValue.mentions)) {
		return false;
	}

	return true;
}

export type MentionData = {
	kind: MentionKind.User;
	userId: number;
	displayName: string;
};

export function getModel(
	editorState: EditorState
): TextWithMentions {
	let resultText: string = "";
	const mentions: Mention[] = [];

	const {
		text,
		mention,
		hard_break
	} = mentionInputSchema.nodes;

	for (let i = 0; i < editorState.doc.childCount; i++) {
		const node: ProsemirrorNode = editorState.doc
			.child(i);

		switch(node.type) {
			case text:
				resultText += node.text ?? "";
				break;
			case mention:
				const mentionAttributes = node.attrs as MentionNodeAttributes;

				mentions.push(
					resolveMention(mentionAttributes, resultText.length)
				);

				resultText += mentionAttributes.label;

				break;
			case hard_break:
				resultText += "\n";
				break;
			default:
				throw new Error(`Node type ${node.type} is not supported.`);
		}
	}

	return {
		text: resultText,
		mentions
	};
}

export function setModel(
	editorState: EditorState,
	model: TextWithMentions,
	data: readonly MentionData[],
	shouldAddToHistory: boolean = true
): EditorState {
	return editorState.apply(
		editorState.tr
			.setMeta("addToHistory", shouldAddToHistory)
			.replaceWith(
				0,
				editorState.doc.nodeSize - 2,
				getContentNodes(
					model,
					data
				)
			)
	);
}

function getContentNodes(
	model: TextWithMentions,
	data: readonly MentionData[]
): ProsemirrorNode[] {
	const result: ProsemirrorNode[] = [];
	let fromPosition: number = 0;

	const insertTextNodes = (text: string): void => {
		const lines: string[] = text.split("\n");

		for (let i = 0; i < lines.length; i++) {
			const line: string = lines[i];
			if (line.length > 0) {
				result.push(mentionInputSchema.text(line));
			}

			if (i < lines.length - 1) {
				result.push(
					mentionInputSchema.nodes
						.hard_break
						.create()
				);
			}
		}
	};

	for (let i = 0; i < model.mentions.length; i++) {
		const mention: Mention = model.mentions[i];
		const text: string = model.text
			.substring(fromPosition, mention.from);

		insertTextNodes(text);

		const mentionAttributes: MentionNodeAttributes = resolveMentionAttributes(
			model.text,
			mention,
			data
		);

		result.push(
			mentionInputSchema.nodes
				.mention
				.create(mentionAttributes)
		);

		fromPosition = mention.to;
	}

	insertTextNodes(
		model.text
			.substring(fromPosition)
	);

	return result;
}

function resolveMentionAttributes(
	text: string,
	mention: Mention,
	data: readonly MentionData[]
): MentionNodeAttributes {
	const mentionData: MentionData | undefined = findMentionData(mention, data);

	if (!mentionData) {
		return resolveStaleMentionAttributes(text, mention);
	}

	return {
		isStale: false,
		type: mentionKindToMentionType[mention.kind],
		label: getLabel(mentionData),
		value: serializeValue(mention)
	};
}

function resolveStaleMentionAttributes(
	text: string,
	mention: Mention
): MentionNodeAttributes {
	return {
		isStale: true,
		type: mentionKindToMentionType[mention.kind],
		label: text
			.substring(mention.from, mention.to),
		value: serializeValue(mention)
	};
}

function serializeValue(mention: Mention): string {
	switch(mention.kind) {
		case MentionKind.User:
			return mention.userId
				.toString(10);
		default:
			never(mention.kind);
	}
}

function findMentionData(mention: Mention, data: readonly MentionData[]): MentionData | undefined {
	switch(mention.kind) {
		case MentionKind.User:
			return data.find(
				d => d.kind === MentionKind.User
					&& d.userId === mention.userId
			);
		default:
			never(mention.kind);
	}
}

function getLabel(mentionData: MentionData): string {
	switch(mentionData.kind) {
		case MentionKind.User:
			return mentionData.displayName;
		default:
			never(mentionData.kind);
	}
}

function resolveMention(
	mentionAttributes: MentionNodeAttributes,
	positionInText: number
): Mention {
	const from: number = positionInText;
	const to: number = positionInText + mentionAttributes.label.length;

	switch(mentionAttributes.type) {
		case userMentionType:
			return {
				kind: MentionKind.User,
				from,
				to,
				userId: parseInt(
					ensureNotNil(mentionAttributes.value),
					10
				)
			};
		default:
			throw new Error(`Unknown mention type ${mentionAttributes.type}`);
	}
}

const mentionKindToMentionType: {[kind in MentionKind]: string} = {
	[MentionKind.User]: userMentionType
};